/**
 * Admin API client (read-only).
 *
 * Base URL is fixed at build time (localhost → :3100, else api.ulaim.com) and
 * is intentionally NOT user-configurable — exposing it would let an attacker
 * point the admin shell at a honeypot and harvest the key. The admin key lives
 * in sessionStorage only (tab-scoped, cleared on close) and rides as the
 * `X-Admin-Key` header. Responses use the `{ success, data }` envelope.
 */

const SS_KEY_ADMIN = 'ulaim_admin_key';
const PROD_API = 'https://api.ulaim.com';
const DEV_API = 'http://localhost:3100';

function isLocalHost(): boolean {
  const h = location.hostname;
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]';
}
export function resolveApiBase(): string {
  return isLocalHost() ? DEV_API : PROD_API;
}
export function shouldShowEndpoint(): boolean {
  return isLocalHost();
}
export function getAdminKey(): string | null {
  return sessionStorage.getItem(SS_KEY_ADMIN);
}
export function setAdminKey(key: string): void {
  sessionStorage.setItem(SS_KEY_ADMIN, key);
}
export function clearAdminKey(): void {
  sessionStorage.removeItem(SS_KEY_ADMIN);
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

async function get<T>(path: string, params: Record<string, string | number | undefined> = {}): Promise<T> {
  const key = getAdminKey();
  if (!key) throw new ApiError(401, 'MISSING_ADMIN_KEY', 'Admin key not configured');
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '' && v !== null)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');
  const url = `${resolveApiBase()}${path}${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { 'X-Admin-Key': key, Accept: 'application/json' } }).catch((err) => {
    throw new ApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network error');
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
    throw new ApiError(res.status, body.error ?? 'HTTP_ERROR', body.message ?? res.statusText);
  }
  const body = (await res.json()) as { success?: boolean; data?: T };
  if (body.success === false || !('data' in body)) {
    throw new ApiError(res.status, 'UNKNOWN', 'Malformed response');
  }
  return body.data as T;
}

export async function ping(): Promise<boolean> {
  try {
    const key = getAdminKey();
    if (!key) return false;
    const res = await fetch(`${resolveApiBase()}/v1/admin/ping`, { headers: { 'X-Admin-Key': key } });
    return res.ok;
  } catch {
    return false;
  }
}

// ── Types ─────────────────────────────────────────────────────────────────────

export type Country = 'jordan' | 'iraq';
export type CountryScope = Country | 'all';

export interface CountrySummary { chat: number; rag: number; total: number }
export interface RagCaveats {
  uniqueUsersAreIps: boolean;
  noSubjectShare: number;
  noTokensOrCost: boolean;
  ragTrackedSince: string;
  ragMayOverlapOnFailure: boolean;
  questionsMinCount: number;
}
export interface AdminSummary {
  jordan: CountrySummary;
  iraq: CountrySummary;
  all: CountrySummary;
  meta: { refreshedAt: string; ragTrackedSince: string; exactCountsHealthy: boolean; caveats: RagCaveats };
}

export interface RagOverview {
  refreshedAt: string;
  country: CountryScope;
  exact: {
    total: number;
    byCountry: { jordan: number; iraq: number };
    byEndpoint: { rag: number; vision: number };
    exactCountsHealthy: boolean;
  };
  snapshot: {
    snapshotRows: number;
    coverage: number;
    uniqueUsers: number;
    imageRequests: number;
    bySubject: Array<{ collectionId: string; count: number }>;
    byGrade: Array<{ grade: string; count: number; uniqueUsers: number }>;
    byLanguage: Array<{ language: string; count: number }>;
    subjectCoverage: number;
  };
  caveats: RagCaveats;
}

export interface RagTimeseriesPoint { bucket: string; count: number; uniqueUsers: number }
export interface RagTopQuestions { country: CountryScope; minCount: number; questions: Array<{ question: string; count: number }> }

export interface TimeseriesPoint {
  bucket: string;
  messages: number;
  userMessages: number;
  assistantMessages: number;
  activeUsers: number;
}

export interface HealthMetric {
  key: string;
  label: string;
  description: string;
  unit: 'percent' | 'number' | 'minutes';
  current: number;
  average7d: number;
  changePct: number;
  trend: 'up' | 'flat' | 'down';
  sparkline: Array<{ date: string; value: number }>;
}

export interface Added {
  users: number;
  sessions: number;
  messages: number;
  userMessages: number;
}

export interface OverviewStats {
  refreshedAt: string;
  dataQuality: { exactCountsHealthy: boolean; snapshotRows: number; collectionRows: number };
  totals: {
    userMessages: number;
    assistantMessages: number;
    totalMessages: number;
    users: number;
    authenticatedUsers: number;
    guestIdentities: number;
    sessions: number;
  };
  now: {
    activeUsers10s: number;
    activeUsers1m: number;
    userMessagesLast24h: number;
    messagesLastHour: number;
    newUsers24h: number;
    peakConcurrency24h: number;
    growthVelocityWoW: number;
    growthVelocityHasBaseline: boolean;
  };
  engagement: {
    avgMessagesPerUser: number;
    avgMessagesPerSession: number;
    avgSessionsPerUser: number;
    sessionDepth: Array<{ bucket: string; count: number; userMessages: number }>;
    userVolume: Array<{ bucket: string; count: number; userMessages: number }>;
    sessionDurations: Array<{ bucket: string; count: number }>;
    newUsersByDay: Array<{ date: string; count: number }>;
    topSessions: Array<{
      rank: number;
      sessionTag: string;
      userTag: string;
      userMessages: number;
      assistantMessages: number;
      durationMinutes: number;
    }>;
  };
  growth: {
    added24h: Added;
    added7d: Added;
    added30d: Added;
    allTime: {
      peakConcurrency: number;
      peakConcurrencyAt: string;
      busiestDayUserMessages: number;
      busiestDayAt: string;
      longestSessionMinutes: number;
      deepestSessionUserMessages: number;
    };
  };
  productHealth: HealthMetric[];
}

export interface LivePulse {
  timestamp: string;
  activeUsers1m: number;
  userMessagesLast24h: number;
  userMessagesTotal: number;
  perMinute60: number[];
}

export interface CollectionInfo {
  name: string;
  pointCount: number;
  vectorsCount?: number | null;
  indexedFields?: string[];
  status?: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

export const fetchSummary = () => get<AdminSummary>('/v1/admin/summary');

export interface CombinedUsers {
  jordan: number;
  iraq: number;
  all: number;
  returning?: { jordan: number; iraq: number; all: number };
  refreshedAt: string;
  basis: string;
}
export const fetchCombinedUsers = () => get<CombinedUsers>('/v1/admin/users/combined');

export const fetchRagOverview = (country: CountryScope) => get<RagOverview>('/v1/admin/rag/overview', { country });
export const fetchRagTimeseries = (granularity: 'hour' | 'day', country: CountryScope, from?: string, to?: string) =>
  get<{ points: RagTimeseriesPoint[] }>('/v1/admin/rag/timeseries', { granularity, country, ...(from ? { from } : {}), ...(to ? { to } : {}) });
export const fetchRagTopQuestions = (country: CountryScope, limit = 25) =>
  get<RagTopQuestions>('/v1/admin/rag/top-questions', { country, limit });

export const fetchOverview = () => get<OverviewStats>('/v1/admin/overview');
export const fetchIraqOverview = () => get<OverviewStats>('/v1/admin/iraq/overview');
export const fetchTimeseries = (granularity: 'hour' | 'day', from?: string, to?: string) =>
  get<{ points: TimeseriesPoint[] }>('/v1/admin/timeseries', { granularity, ...(from ? { from } : {}), ...(to ? { to } : {}) });
export const fetchIraqTimeseries = (granularity: 'hour' | 'day', from?: string, to?: string) =>
  get<{ points: TimeseriesPoint[] }>('/v1/admin/iraq/timeseries', { granularity, ...(from ? { from } : {}), ...(to ? { to } : {}) });

export const fetchCollections = () => get<{ collections: CollectionInfo[] }>('/v1/admin/collections');

/**
 * Live pulse via SSE (Jordan free-chat). One backend loop fans out to all
 * viewers — cost is O(1) in viewers. Falls back to 3 s polling if the stream
 * drops. Returns a cleanup function.
 */
export function subscribeLive(onPulse: (p: LivePulse) => void, country: Country = 'jordan'): () => void {
  const key = getAdminKey();
  const base = resolveApiBase();
  if (!key) return () => {};
  const streamPath = country === 'iraq' ? '/v1/admin/iraq/stream' : '/v1/admin/stream';
  const livePath = country === 'iraq' ? '/v1/admin/iraq/live' : '/v1/admin/live';

  let aborted = false;
  let fallback: number | null = null;
  const ctrl = new AbortController();

  void (async () => {
    try {
      const res = await fetch(`${base}${streamPath}`, {
        headers: { 'X-Admin-Key': key, Accept: 'text/event-stream' },
        signal: ctrl.signal,
        credentials: 'omit',
      });
      if (!res.ok || !res.body) throw new Error(`stream http ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (!aborted) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const dataLine = frame.split('\n').find((l) => l.startsWith('data: '));
          if (!dataLine) continue;
          try {
            onPulse(JSON.parse(dataLine.slice(6)) as LivePulse);
          } catch {
            /* ignore malformed frame */
          }
        }
      }
    } catch {
      if (aborted) return;
      if (!fallback) {
        const poll = async () => {
          try {
            const r = await fetch(`${base}${livePath}`, { headers: { 'X-Admin-Key': key } });
            if (r.ok) {
              const body = (await r.json()) as { data?: LivePulse };
              if (body.data) onPulse(body.data);
            }
          } catch {
            /* swallow */
          }
        };
        void poll();
        fallback = window.setInterval(poll, 3_000);
      }
    }
  })();

  return () => {
    aborted = true;
    ctrl.abort();
    if (fallback) clearInterval(fallback);
  };
}
